import dmSansItalicLatinExtUrl from "../fonts/dm-sans-italic-latin-ext.woff2?url";
import dmSansItalicLatinUrl from "../fonts/dm-sans-italic-latin.woff2?url";
import dmSansLatinExtUrl from "../fonts/dm-sans-latin-ext.woff2?url";
import dmSansLatinUrl from "../fonts/dm-sans-latin.woff2?url";
import sourceSerifItalicLatinExtUrl from "../fonts/source-serif-4-italic-latin-ext.woff2?url";
import sourceSerifItalicLatinUrl from "../fonts/source-serif-4-italic-latin.woff2?url";
import sourceSerifLatinExtUrl from "../fonts/source-serif-4-latin-ext.woff2?url";
import sourceSerifLatinUrl from "../fonts/source-serif-4-latin.woff2?url";

/**
 * The block webfonts, provisioned by the host.
 *
 * A canvas block is a sandboxed `srcdoc` iframe with an opaque origin, and an
 * opaque origin gets no HTTP cache partition: every block — and every remount
 * of the same block (a theme flip, a lightbox, a content change) — would
 * fetch the fonts from the network again, arriving after first paint and
 * re-wrapping the text as they swap in. So the HOST loads the files once
 * (same-origin, ordinarily cached) and hands the bytes to each frame over the
 * runtime bridge; the frame registers them with `FontFace` before its first
 * paint. No per-frame network, no swap.
 *
 * Latin and latin-ext subsets of the two variable families the token stacks
 * name (`--font-sans`, `--font-serif`), the same files and unicode ranges
 * Google Fonts serves. `--font-mono` stays a system stack. Sizes: ~675 KB
 * total — the bytes a frame would hold after downloading them itself.
 */
export interface BlockFontFaceSpec {
  family: string;
  style: "normal" | "italic";
  /** Variable weight range, as a `font-weight` descriptor. */
  weight: string;
  unicodeRange: string;
  url: string;
}

/** One provisioned face: the descriptors plus the font file's bytes. */
export interface BlockFontFacePayload {
  family: string;
  style: "normal" | "italic";
  weight: string;
  unicodeRange: string;
  data: ArrayBuffer;
}

const LATIN_RANGE =
  "U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD";
const LATIN_EXT_RANGE =
  "U+0100-02BA, U+02BD-02C5, U+02C7-02CC, U+02CE-02D7, U+02DD-02FF, U+0304, U+0308, U+0329, U+1D00-1DBF, U+1E00-1E9F, U+1EF2-1EFF, U+2020, U+20A0-20AB, U+20AD-20C0, U+2113, U+2C60-2C7F, U+A720-A7FF";

export const BLOCK_FONT_FACES: readonly BlockFontFaceSpec[] = [
  {
    family: "DM Sans",
    style: "normal",
    weight: "100 1000",
    unicodeRange: LATIN_RANGE,
    url: dmSansLatinUrl,
  },
  {
    family: "DM Sans",
    style: "normal",
    weight: "100 1000",
    unicodeRange: LATIN_EXT_RANGE,
    url: dmSansLatinExtUrl,
  },
  {
    family: "DM Sans",
    style: "italic",
    weight: "100 1000",
    unicodeRange: LATIN_RANGE,
    url: dmSansItalicLatinUrl,
  },
  {
    family: "DM Sans",
    style: "italic",
    weight: "100 1000",
    unicodeRange: LATIN_EXT_RANGE,
    url: dmSansItalicLatinExtUrl,
  },
  {
    family: "Source Serif 4",
    style: "normal",
    weight: "200 900",
    unicodeRange: LATIN_RANGE,
    url: sourceSerifLatinUrl,
  },
  {
    family: "Source Serif 4",
    style: "normal",
    weight: "200 900",
    unicodeRange: LATIN_EXT_RANGE,
    url: sourceSerifLatinExtUrl,
  },
  {
    family: "Source Serif 4",
    style: "italic",
    weight: "200 900",
    unicodeRange: LATIN_RANGE,
    url: sourceSerifItalicLatinUrl,
  },
  {
    family: "Source Serif 4",
    style: "italic",
    weight: "200 900",
    unicodeRange: LATIN_EXT_RANGE,
    url: sourceSerifItalicLatinExtUrl,
  },
];

let inflight: Promise<BlockFontFacePayload[]> | null = null;

/**
 * The provisioned faces with their bytes, fetched once per page and shared by
 * every block on it. A failed load is not remembered — the next block asks
 * again — and a frame that never receives fonts simply renders on the system
 * stacks after its own short wait.
 */
export function loadBlockFonts(): Promise<BlockFontFacePayload[]> {
  if (!inflight) {
    inflight = Promise.all(
      BLOCK_FONT_FACES.map(async (spec) => {
        const response = await fetch(spec.url);
        if (!response.ok) {
          throw new Error(`block font ${spec.url}: HTTP ${response.status}`);
        }
        const data = await response.arrayBuffer();
        return {
          family: spec.family,
          style: spec.style,
          weight: spec.weight,
          unicodeRange: spec.unicodeRange,
          data,
        };
      }),
    ).catch((error: unknown) => {
      inflight = null;
      throw error;
    });
  }
  return inflight;
}

/** Test seam: forget a previous load. */
export function resetBlockFontsForTests(): void {
  inflight = null;
}
